import React from 'react';
import { StockDataPoint } from '../types/StockData';

interface FlowAlertsTabProps {
  selectedDataPoint: StockDataPoint | null;
}

const FlowAlertsTab: React.FC<FlowAlertsTabProps> = ({ selectedDataPoint }) => {
  if (!selectedDataPoint?.flowAlerts || selectedDataPoint.flowAlerts.length === 0) {
    return (
      <div className="text-center py-4 text-gray-500">
        No flow alerts available for this time window
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {selectedDataPoint.flowAlerts.map((alert, index: number) => {
        // Use gamma chart colors: green for calls, red for puts
        const isCall = alert.type?.toLowerCase() === 'call';
        const bgColor = isCall ? 'bg-green-50' : 'bg-red-50';
        const borderColor = isCall ? 'border-green-400' : 'border-red-400';
        const textColor = isCall ? 'text-green-800' : 'text-red-800';
        
        return (
        <div key={index} className={`${bgColor} border-l-4 ${borderColor} p-3 rounded`}>
          <div className="flex justify-between items-start mb-2">
            <div className="flex flex-wrap items-center gap-1">
              <span className={`font-semibold ${textColor}`}>
                {alert.strike} {alert.expiry ? new Date(alert.expiry).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: '2-digit' }) : ''} {alert.type.toUpperCase()}
              </span>
              <span className={`px-2 py-1 text-xs rounded ${
                alert.sentiment === 'BULLISH' ? 'bg-green-100 text-green-800' :
                alert.sentiment === 'BEARISH' ? 'bg-red-100 text-red-800' :
                'bg-gray-100 text-gray-800'
              }`}>
                {alert.sentiment}
              </span>
              {/* Special Indicators */}
              {alert.has_floor && (
                <span className="px-2 py-1 text-xs rounded bg-purple-100 text-purple-800 font-medium">
                  FLOOR
                </span>
              )}
              {alert.has_multileg && (
                <span className="px-2 py-1 text-xs rounded bg-blue-100 text-blue-800 font-medium">
                  MULTILEG
                </span>
              )}
              {alert.has_sweep && (
                <span className="px-2 py-1 text-xs rounded bg-yellow-100 text-yellow-800 font-medium">
                  SWEEP
                </span>
              )}
            </div>
            <span className="text-sm text-gray-600">
              {new Date(alert.created_at).toLocaleTimeString('en-US', { timeZone: 'America/New_York' })}
            </span>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs text-gray-700">
            <div><span className="font-medium">Strike:</span> ${alert.strike || 'N/A'}</div>
            <div><span className="font-medium">Price:</span> ${alert.price?.toFixed(2) || 'N/A'}</div>
            <div><span className="font-medium">Volume:</span> {alert.volume?.toLocaleString() || 'N/A'}</div>
            <div><span className="font-medium">Premium:</span> ${alert.total_premium?.toLocaleString() || 'N/A'}</div>
            <div><span className="font-medium">Bid Premium:</span> ${alert.total_bid_side_prem?.toLocaleString() || 'N/A'}</div>
            <div><span className="font-medium">Ask Premium:</span> ${alert.total_ask_side_prem?.toLocaleString() || 'N/A'}</div>
            <div><span className="font-medium">Size:</span> {alert.total_size || 'N/A'}</div>
            <div><span className="font-medium">Alert Rule:</span> {alert.alert_rule || 'N/A'}</div>
          </div>
        </div>
        );
      })}
    </div>
  );
};

export default FlowAlertsTab;
